import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { useConfigStore } from "@/stores/config";
import { useInspectionStore } from "@/stores/inspection";
import { INSPECTION_COMMENTS, STRUCTURE_UNIT_TYPE } from "@/constants/Inspections";
import { getReportFooter, getReportHeader } from "@/reports/ReportUtil";
import { styles } from "@/reports/reportStyles";
import { convertToInt } from "@/composables/util";

function setBearingsDocDefinition(structure) {
  const configStore = useConfigStore();
  const bearingDetailsOne = [];
  bearingDetailsOne.push(getBearingsOneHeader());
  getBearingOne(bearingDetailsOne, structure);

  let docDefinition = {
    header: function () {
      return getReportHeader("Bearings Page", structure);
    },
    footer: function (currentPage, pageCount) {
      return getReportFooter(currentPage, pageCount);
    },
    pageMargins: [20, 70, 20, 50],
    content: [
      { text: "", pageHeaderText: "Bearings Page" },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Bearing Conditions",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
      },
      {
        columns: [
          {
            width: "35%",
            text: "B.C.07 Overall Bearing Condition Rating: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.BEARING_CONDITION_RATING,
              structure.userInspection?.overallBearingCondRating,
            ),
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        text: "Overall Bearing Notes: ",
        bold: true,
        margin: [0, 3, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.BEARING_OVERALL_COMMENTS,
        )?.notes,
        margin: [0, 0, 0, 5],
      },
      {
        table: {
          widths: ["100%"],
          headerRows: 0,
          body: [
            ["Legend:"],
            [
              {
                table: {
                  headerRows: 0,
                  widths: ["24%", "25%", "25%", "*"],
                  body: [
                    [
                      "BRG.01 Bearing Key",
                      "BRG.04 Bearing Location",
                      "BRG.07 Corrosion?",
                      "BRG.10 Loss of Bearing Area?",
                    ],
                    [
                      "BRG.02 Record Key",
                      "BRG.05 Bearing Count",
                      "BRG.08 Alignment Issues?",
                      "BRG.11 Condition Rating",
                    ],
                    ["BRG.03 Bearing Type", "BRG.06 Bearing Movement?", "BRG.09 Anchor Bolt Issues?", ""],
                  ],
                },
                layout: "noBorders",
              },
            ],
          ],
        },
        layout: {
          hLineWidth: function (i, node) {
            return i === 0 || i === node.table.body.length ? 1 : 0;
          },
          vLineWidth: function (i, node) {
            return i === 0 || i === node.table.widths.length ? 1 : 0;
          },
          hLineColor: function () {
            return "black";
          },
          vLineColor: function () {
            return "black";
          },
        },
        unbreakable: true,
      },
      {
        table: {
          headerRows: 1,
          widths: ["auto", "15%", "15%", "*", "*", "*", "*", "*", "*", "15%"],
          body: bearingDetailsOne,
        },
        margin: [0, 5, 0, 0],
      },
    ],
    styles: styles,
    defaultStyle: {
      fontSize: 10,
    },
  };
  return docDefinition;
}

function getBearingsOneHeader() {
  return [
    {
      text: "BRG.01-BRG.02",
      style: ["tableHeader"],
    },
    {
      text: "BRG.03",
      style: ["tableHeader"],
    },
    {
      text: "BRG.04",
      style: ["tableHeader"],
    },
    {
      text: "BRG.05",
      style: ["tableHeader"],
    },
    {
      text: "BRG.06",
      style: ["tableHeader"],
    },
    {
      text: "BRG.07",
      style: ["tableHeader"],
    },
    {
      text: "BRG.08",
      style: ["tableHeader"],
    },
    {
      text: "BRG.09",
      style: ["tableHeader"],
    },
    {
      text: "BRG.10",
      style: ["tableHeader"],
    },
    {
      text: "BRG.11",
      style: ["tableHeader"],
    },
  ];
}

function getBearingLocationList() {
  const configStore = useConfigStore();
  const inspectionStore = useInspectionStore();
  return inspectionStore
    .getFilteredStructureUnits([STRUCTURE_UNIT_TYPE.APRAS_SPAN], false)
    ?.map((b) => ({
      title: `${configStore.getReferenceValue(REFERENCE_TABLE.STRUCTURE_UNIT_TYPE, b?.legacyStructureType, true)}-${
        b?.strUnitLabel
      }`,
      value: b?.strUnitKey,
    }))
    .sort((a, b) => a?.title?.localeCompare(b?.title));
}

function getBearingLocations(value) {
  const bearingLocations = getBearingLocationList();
  const configStore = useConfigStore();
  if (
    bearingLocations?.find((a) => a.value === value.bearingLocation)?.title &&
    configStore.getReferenceValue(REFERENCE_TABLE.BEARING_SUBSTRUCTURE_UNIT, value.bearingSubstrUnit)?.split(" ")[2]
  ) {
    return (
      bearingLocations?.find((a) => a.value === value.bearingLocation)?.title +
      "-" +
      configStore.getReferenceValue(REFERENCE_TABLE.BEARING_SUBSTRUCTURE_UNIT, value.bearingSubstrUnit).split(" ")[2]
    );
  } else {
    return "";
  }
}

function getBearingOne(bearingDetailsOne, structure) {
  const configStore = useConfigStore();
  const bearings =
    structure?.structureBearings?.sort(function (a, b) {
      if (a?.bearingId == b?.bearingId) {
        return convertToInt(b?.recordKey) - convertToInt(a?.recordKey);
      } else {
        return a?.bearingId - b?.bearingId;
      }
    }) || [];
  for (const Bearing of bearings ?? []) {
    bearingDetailsOne.push(
      [
        {
          text: Bearing?.bearingId + "-" + Bearing?.recordKey,
          style: ["tableBody"],
        },
        {
          text: configStore.getReferenceValue(REFERENCE_TABLE.BEARING_TYPE, Bearing?.bearingType),
          style: ["tableBody"],
        },
        {
          text: getBearingLocations(Bearing),
          style: ["tableBody"],
        },
        {
          text: Bearing?.bearingCount,
          style: ["tableBody"],
        },
        {
          text: Bearing?.bearingMovement,
          style: ["tableBody"],
        },
        {
          text: getShortCode(Bearing?.corrosion),
          style: ["tableBody"],
        },
        {
          text: getShortCode(Bearing?.alignmentIssues),
          style: ["tableBody"],
        },
        {
          text: getShortCode(Bearing?.anchorBolt),
          style: ["tableBody"],
        },
        {
          text: getShortCode(Bearing?.bearingArea),
          style: ["tableBody"],
        },
        {
          text: configStore.getReferenceValue(REFERENCE_TABLE.BEARING_CONDITION_RATING, Bearing?.conditionRating),
          style: ["tableBody"],
        },
      ],
      //Joint notes
      [
        {
          colSpan: "10",
          text: [{ text: "BRG.12 Bearing Notes: ", bold: true }, Bearing?.bearingComment],
        },
      ],
    );
  }
}

function getShortCode(value) {
  if (value == 0) {
    return "N";
  } else if (value == 1) {
    return "Y-O";
  } else if (value == 2) {
    return "Y-M";
  } else {
    return "";
  }
}

export { setBearingsDocDefinition };
